import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import {
  BriefcaseFill,
  CheckCircleFill,
  ChevronDown,
  ChevronRight,
  InfoCircleFill,
  PeopleFill,
  PersonBadgeFill,
  PersonWorkspace,
  ShieldLockFill,
  XCircleFill,
} from "react-bootstrap-icons";
import styles from "./UserGuideRolePermission.module.css";
import {
  ACTION_TYPES,
  CASE_MANAGER_CUSTOMIZATION_DATA,
  CONFIGURE_PERMISSION_STEPS,
  GUIDE_CONTENT,
  IMPORTANT_NOTES,
  PERMISSION_CATEGORIES,
  ROLE_GUIDE_DATA,
} from "./UserGuideData";

const TABS = {
  ROLES: "roles",
  PERMISSION_CATEGORIES: "permission_categories",
  ACTION_TYPES: "action_types",
  CONFIGURATION: "configuration",
};

function UserGuideRolePermissions({ show, onHide }) {
  const [activeTab, setActiveTab] = useState(TABS.ROLES);
  const [selectedRoleId, setSelectedRoleId] = useState(ROLE_GUIDE_DATA[0]?.id);

  const toggleRole = (roleId) => {
    setSelectedRoleId((prev) => (prev === roleId ? null : roleId));
  };

  const getRoleIcon = (roleId) => {
    const iconProps = { size: 12 };
    switch (roleId) {
      case "system-admin":
        return <ShieldLockFill {...iconProps} />;
      case "system-user":
        return <PersonWorkspace {...iconProps} />;
      case "administrator":
        return <PersonBadgeFill {...iconProps} />;
      case "manager":
        return <BriefcaseFill {...iconProps} />;
      case "case-manager":
        return <PeopleFill {...iconProps} />;
      default:
        return <PersonBadgeFill {...iconProps} />;
    }
  };

  const getScopeClass = (scope) => {
    const normalizedScope = String(scope || "").toLowerCase();
    if (normalizedScope.includes("system")) return styles.scopePillSystem;
    if (normalizedScope.includes("entity")) return styles.scopePillEntity;
    if (normalizedScope.includes("client")) return styles.scopePillClient;
    if (normalizedScope.includes("device")) return styles.scopePillDevice;
    return styles.scopePillDefault;
  };

  const renderRolesTab = () => {
    return (
      <div className={styles.tabPanel}>
        <p className={styles.rolesHint}>Example role to see what they can and can't do.</p>
        <div className={styles.roleList}>
          {ROLE_GUIDE_DATA.map((role) => {
            const isActive = selectedRoleId === role.id;
            const scopes = String(role.scope || "")
              .split(",")
              .map((scope) => scope.trim())
              .filter(Boolean);
            return (
              <article key={role.id} className={styles.roleCard}>
                <button
                  type="button"
                  className={styles.roleButton}
                  onClick={() => toggleRole(role.id)}
                >
                  <div className={styles.roleButtonHead}>
                    <span className={styles.roleAvatar}>{getRoleIcon(role.id)}</span>
                    <div className={styles.roleHeaderText}>
                      <span>{role.title}</span>
                      <div className={styles.roleScopePills}>
                        {scopes.map((scope) => (
                          <span
                            key={`${role.id}-${scope}`}
                            className={`${styles.scopePill} ${getScopeClass(scope)}`}
                          >
                            Scope: {scope}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <span className={styles.roleChevron}>
                    {isActive ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                  </span>
                </button>
                {isActive && (
                  <div className={styles.roleDetails}>
                    <p className={styles.roleDescription}>{role.description}</p>
                    <div className={styles.checkGrid}>
                      <div className={styles.checkColumn}>
                        <h5>Can do</h5>
                        <ul>
                          {role.canDo.map((item) => (
                            <li key={item}>
                              <CheckCircleFill size={12} />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className={styles.checkColumn}>
                        <h5>Can't do</h5>
                        <ul>
                          {role.cannotDo.map((item) => (
                            <li key={item}>
                              <XCircleFill size={12} />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </div>

        <section className={styles.infoBlock}>
          <h4>Who can customize the Case Manager role permissions?</h4>
          <ul>
            {CASE_MANAGER_CUSTOMIZATION_DATA.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>
      </div>
    );
  };

  const renderPermissionCategoryTab = () => {
    return (
      <div className={styles.tabPanel}>
        <h4>Permission Categories</h4>
        <p className={styles.sectionText}>
          Permissions are organized into functional categories, each containing
          sub-groups of related permissions that can be independently enabled or
          disabled.
        </p>
        <div className={styles.categoryList}>
          {PERMISSION_CATEGORIES.map((category) => (
            <article key={category.title} className={styles.card}>
              <h5>{category.title}</h5>
              <p>{category.description}</p>
            </article>
          ))}
        </div>
      </div>
    );
  };

  const renderActionTypesTab = () => {
    return (
      <div className={styles.tabPanel}>
        <h4>Action Types</h4>
        <p className={styles.sectionText}>
          Each permission represents a specific action a user can take on the
          portal.
        </p>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Action</th>
                <th>What it means</th>
                <th>Example</th>
              </tr>
            </thead>
            <tbody>
              {ACTION_TYPES.map((actionType) => (
                <tr key={actionType.action}>
                  <td>{actionType.action}</td>
                  <td>{actionType.meaning}</td>
                  <td>{actionType.example}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderConfigurationTab = () => {
    return (
      <div className={styles.tabPanel}>
        <h4>How to Configure Permissions</h4>
        <p className={styles.sectionText}>
          Follow these steps to set up or update role permissions for a specific
          client or entity.
        </p>
        <div className={styles.configureStepList}>
          {CONFIGURE_PERMISSION_STEPS.map((step, index) => (
            <article key={step.title} className={styles.configureStepCard}>
              <div className={styles.configureStepHeader}>
                <span className={styles.configureStepNumber}>{index + 1}</span>
                <h5>{step.title}</h5>
              </div>
              <p>{step.description}</p>
              {Array.isArray(step.bullets) && step.bullets.length > 0 && (
                <ul className={styles.configureSubList}>
                  {step.bullets.map((bullet) => (
                    <li key={bullet}>{bullet}</li>
                  ))}
                </ul>
              )}
            </article>
          ))}
        </div>
        <section className={styles.notePanel}>
          <h4>
            <InfoCircleFill size={12} />
            Important Notes
          </h4>
          <ul className={styles.noteList}>
            {IMPORTANT_NOTES.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        </section>
      </div>
    );
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      backdropClassName={styles.guideBackdrop}
      dialogClassName={styles.dialog}
      contentClassName={styles.content}
    >
      <Modal.Header closeButton className={styles.header}>
        <Modal.Title className={styles.title}>{GUIDE_CONTENT.title}</Modal.Title>
      </Modal.Header>
      <Modal.Body className={styles.body}>
        <section className={styles.introSection}>
          <h4>Overview</h4>
          <p>{GUIDE_CONTENT.overview}</p>
        </section>
        <section className={styles.introSection}>
          <h4>User Roles</h4>
          <p>{GUIDE_CONTENT.userRolesIntro}</p>
        </section>
        <section className={styles.tipBox}>
          <h4>Understanding Scope</h4>
          <p>
            <strong>Tip:</strong> {GUIDE_CONTENT.scopeTip}
          </p>
        </section>

        <div className={styles.tabHeader}>
          <button
            type="button"
            className={`${styles.tabButton} ${
              activeTab === TABS.ROLES ? styles.tabButtonActive : ""
            }`}
            onClick={() => setActiveTab(TABS.ROLES)}
          >
            Roles
          </button>
          <button
            type="button"
            className={`${styles.tabButton} ${
              activeTab === TABS.PERMISSION_CATEGORIES
                ? styles.tabButtonActive
                : ""
            }`}
            onClick={() => setActiveTab(TABS.PERMISSION_CATEGORIES)}
          >
            Permission categories
          </button>
          <button
            type="button"
            className={`${styles.tabButton} ${
              activeTab === TABS.ACTION_TYPES ? styles.tabButtonActive : ""
            }`}
            onClick={() => setActiveTab(TABS.ACTION_TYPES)}
          >
            Action types
          </button>
          <button
            type="button"
            className={`${styles.tabButton} ${
              activeTab === TABS.CONFIGURATION ? styles.tabButtonActive : ""
            }`}
            onClick={() => setActiveTab(TABS.CONFIGURATION)}
          >
            How to configure
          </button>
        </div>

        {activeTab === TABS.ROLES && renderRolesTab()}
        {activeTab === TABS.PERMISSION_CATEGORIES && renderPermissionCategoryTab()}
        {activeTab === TABS.ACTION_TYPES && renderActionTypesTab()}
        {activeTab === TABS.CONFIGURATION && renderConfigurationTab()}
      </Modal.Body>
    </Modal>
  );
}

export default UserGuideRolePermissions;